import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, Routes, Route } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { NATIVE_APP_REGISTRY } from '../apps/registry'
import { Header } from './Header'

interface Application {
  id: string
  name: string
  description: string
  url: string
  icon_url: string
  app_type?: 'native' | 'external'
}


export const AppView: React.FC = () => {
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const { user, getSessionTokens, signOut } = useAuth()
  const [application, setApplication] = useState<Application | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [fullName, setFullName] = useState<string | null>(null)
  const [searchParams] = useSearchParams()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const sessionStartRef = useRef<Date | null>(null)
  const hasLoggedAccess = useRef<boolean>(false)

  // Compute iframe URL: use deepLink if provided and origin matches the app URL
  const iframeSrc = useMemo(() => {
    if (!application?.url) return ''
    const deepLink = searchParams.get('deepLink')
    if (!deepLink) return application.url
    try {
      const appOrigin = new URL(application.url).origin
      const deepLinkOrigin = new URL(deepLink).origin
      if (appOrigin === deepLinkOrigin) return deepLink
    } catch {
      // Invalid URL — fall through
    }
    return application.url
  }, [application?.url, searchParams])

  // Reset logging flag when appId changes
  useEffect(() => {
    hasLoggedAccess.current = false
  }, [appId])

  useEffect(() => {
    const fetchApplication = async () => {
      if (!user || !appId) return

      try {
        // Always fetch from database to ensure proper permissions
        const { data, error } = await supabase
          .from('applications')
          .select(`
            id,
            name,
            description,
            url,
            icon_url,
            app_type,
            user_app_access!inner(user_id)
          `)
          .eq('id', appId)
          .eq('user_app_access.user_id', user.id)
          .eq('is_active', true)
          .single()

        if (error) {
          if (error.code === 'PGRST116') {
            setError('Application not found or access denied')
          } else {
            setError('Failed to load application')
          }
          console.error('Error fetching application:', error)
        } else {
          setApplication(data)

          // Fetch user's full name from profile
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('full_name')
            .eq('id', user.id)
            .single()
          if (profile?.full_name) {
            setFullName(profile.full_name)
          }

          // Log app access only once per session
          if (!hasLoggedAccess.current) {
            sessionStartRef.current = new Date()
            hasLoggedAccess.current = true
            
            // Use sessionStorage to prevent duplicate logging on component re-renders
            const sessionKey = `app_logged_${data.id}_${user.id}_${Date.now()}`
            const existingLog = sessionStorage.getItem(`app_logged_${data.id}_${user.id}`)
            
            if (!existingLog) {
              // Mark this app as logged for this session
              sessionStorage.setItem(`app_logged_${data.id}_${user.id}`, sessionKey)
              
              setTimeout(async () => {
                try {
                  const { error: logError } = await supabase.from('app_usage_logs').insert({
                    user_id: user.id,
                    application_id: data.id,
                    user_agent: navigator.userAgent,
                    ip_address: null
                  })
                  if (logError) {
                    console.debug('Activity logging failed (this is ok):', logError.message)
                  }
                } catch (err) {
                  console.debug('Activity logging error (this is ok):', err)
                }
              }, 100)
            }
          }
        }
      } catch (err) {
        setError('Failed to load application')
        console.error('Error fetching application:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchApplication()
  }, [user, appId])

  useEffect(() => {
    const sendAuthToken = async () => {
      if (!iframeRef.current || !application || application.app_type === 'native') return

      const { accessToken, refreshToken } = await getSessionTokens()
      if (!accessToken) return

      const targetOrigin = new URL(application.url).origin

      const handleIframeLoad = () => {
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            {
              type: 'AUTH_TOKEN',
              token: accessToken,
              refreshToken: refreshToken,
              user: {
                id: user?.id || '',
                email: user?.email || '',
                fullName: fullName || ''
              }
            },
            targetOrigin
          )
        }
      }

      iframeRef.current.addEventListener('load', handleIframeLoad)

      return () => {
        if (iframeRef.current) {
          iframeRef.current.removeEventListener('load', handleIframeLoad)
        }
      }
    }

    sendAuthToken()
  }, [application, fullName, getSessionTokens, user])

  // Listen for messages from iframe (external apps)
  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      // Validate origin against the application URL
      if (!application?.url) return
      const expectedOrigin = new URL(application.url).origin
      if (event.origin !== expectedOrigin) return

      switch (event.data?.type) {
        case 'NAVIGATE_HOME':
          navigate('/')
          break
        case 'NAVIGATE_PROFILE':
          navigate('/profile')
          break
        case 'SIGN_OUT':
          signOut()
          break
        case 'REQUEST_AUTH_TOKEN':
          // Re-send auth token to iframe on demand
          (async () => {
            const { accessToken, refreshToken } = await getSessionTokens()
            if (accessToken && iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage(
                {
                  type: 'AUTH_TOKEN',
                  token: accessToken,
                  refreshToken: refreshToken,
                  user: {
                    id: user?.id || '',
                    email: user?.email || '',
                    fullName: fullName || ''
                  }
                },
                expectedOrigin
              )
            }
          })()
          break
      }
    }

    window.addEventListener('message', handleIframeMessage)
    return () => window.removeEventListener('message', handleIframeMessage)
  }, [application, navigate, signOut, getSessionTokens, user, fullName])

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: '64px', 
            height: '64px', 
            background: 'white', 
            borderRadius: '50%', 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
            marginBottom: '16px' 
          }}>
            <div style={{ 
              animation: 'spin 1s linear infinite', 
              borderRadius: '50%', 
              height: '32px', 
              width: '32px', 
              border: '2px solid #2563eb', 
              borderBottomColor: 'transparent' 
            }}></div>
          </div>
          <p style={{ 
            color: '#6b7280', 
            fontWeight: '500',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Loading application...
          </p>
        </div>
      </div>
    )
  }

  if (error || !application) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            borderRadius: '24px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            padding: '48px', 
            maxWidth: '448px', 
            margin: '0 auto',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              color: '#111827', 
              marginBottom: '16px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              {error || 'Application not found'}
            </h3>
            <p style={{ 
              color: '#6b7280', 
              marginBottom: '24px',
              fontFamily: 'Lexend Deca, sans-serif'
            }}>
              The application you're looking for doesn't exist or you don't have access to it.
            </p>
            <button 
              onClick={() => navigate('/')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '12px 24px',
                background: 'linear-gradient(to right, #2563eb, #4f46e5)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Render native app if it's a native application
  if (application.app_type === 'native' && application.id) {
    const NativeApp = NATIVE_APP_REGISTRY[application.id]
    if (NativeApp) {
      return (
        <div style={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Header />
          <div style={{ flex: 1, overflow: 'auto' }}>
            <Routes>
              <Route path="/*" element={<NativeApp />} />
            </Routes>
          </div>
        </div>
      )
    } else {
      // Native app component not found in registry
      return (
        <div style={{ 
          minHeight: '100vh', 
          background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' }}>
              Native app not configured
            </h3>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              The native application component is not properly registered.
            </p>
            <button onClick={() => navigate('/')} style={{
              padding: '12px 24px',
              background: 'linear-gradient(to right, #2563eb, #4f46e5)',
              color: 'white',
              fontWeight: '600',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer'
            }}>
              Go to Dashboard
            </button>
          </div>
        </div>
      )
    }
  }

  // Render iframe for external applications
  return (
    <div style={{ height: '100vh', width: '100%' }}>
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title={application.name}
        style={{ width: '100%', height: '100%', border: 'none' }}
        allow="clipboard-read; clipboard-write"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
      />
    </div>
  )
}